import { RAW_MATERIAL_KIND_LABELS } from "@artisancode/api-types";
import { History, Pencil } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn, formatCurrency } from "@/lib/utils";

import { StockRiderQtyCell } from "./stock-rider-qty-cell";
import { STOCK_STATUS_CONFIG, type StockItem } from "./types";

import type { StockRowsProps } from "./stock-desktop-table";

export function StockMobileList({ rows, ...actions }: StockRowsProps) {
  if (rows.length === 0) {
    return (
      <div className="py-12 text-center text-sm text-muted-foreground">
        Tidak ada barang yang cocok dengan kriteria filter.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 p-4">
      {rows.map((item) => (
        <StockCard key={item.id} item={item} {...actions} />
      ))}
    </div>
  );
}

function StockCard({
  item,
  onEditMinStock,
  onViewStockCard,
}: Omit<StockRowsProps, "rows"> & { item: StockItem }) {
  const status = STOCK_STATUS_CONFIG[item.status];

  return (
    <Card className={cn("gap-2 py-3 shadow-xs", status.cardClassName)}>
      <CardContent className="space-y-2.5 px-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <span className="line-clamp-1 text-sm font-semibold text-foreground">
              {item.name}
            </span>
            <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
              {item.sku && (
                <>
                  <span className="font-mono text-[11px]">{item.sku}</span>
                  <span>•</span>
                </>
              )}
              {item.type === "raw_material" && item.kind ? (
                <Badge variant="outline" className="px-1.5 py-0 text-[10px]">
                  {RAW_MATERIAL_KIND_LABELS[item.kind]}
                </Badge>
              ) : (
                <Badge variant="outline" className="px-1.5 py-0 text-[10px]">
                  Produk Siap Jual
                </Badge>
              )}
              <span>•</span>
              <span>{item.categoryName}</span>
            </div>
          </div>
          <Badge
            variant={status.badgeVariant}
            className={cn("shrink-0 text-[10px]", status.className)}
          >
            {status.label}
          </Badge>
        </div>

        <div className="flex items-baseline justify-between border-t border-border/40 pt-2 text-sm">
          <span className="text-xs text-muted-foreground">Total Stok</span>
          <span
            className={cn("font-mono text-base font-bold", status.qtyClassName)}
          >
            {item.currentStock.toLocaleString("id-ID")}{" "}
            <span className="text-xs font-normal text-muted-foreground">
              {item.uomName}
            </span>
          </span>
        </div>

        <div className="grid grid-cols-3 gap-1 rounded-md bg-muted/40 p-2 text-center text-xs">
          <LocationQty label="Di Toko">
            {item.type === "raw_material" && item.kind === "ingredient"
              ? "—"
              : item.onHandQty.toLocaleString("id-ID")}
          </LocationQty>
          <LocationQty label="Di Bar">
            {item.type === "raw_material"
              ? item.kind === "purchased"
                ? "—"
                : item.repackingQty.toLocaleString("id-ID")
              : "—"}
          </LocationQty>
          <LocationQty label="Di Rider">
            <StockRiderQtyCell item={item} />
          </LocationQty>
        </div>

        <div className="flex items-center justify-between pt-1 text-xs">
          <span className="text-muted-foreground">Batas Min. Stok:</span>
          <button
            type="button"
            onClick={() => onEditMinStock?.(item)}
            className="-my-1 inline-flex min-h-[36px] cursor-pointer items-center gap-1 rounded px-1.5 py-1 font-mono font-medium text-foreground hover:bg-muted hover:text-primary"
          >
            <span>
              {item.minStock.toLocaleString("id-ID")} {item.uomName}
            </span>
            <Pencil className="h-3 w-3 text-primary" />
          </button>
        </div>

        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">Nilai Persediaan:</span>
          <span className="font-mono font-semibold text-foreground">
            {formatCurrency(item.totalValue)}
          </span>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border/40 pt-2.5">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onViewStockCard?.(item)}
            className="min-h-[40px] gap-1.5 text-xs text-muted-foreground hover:text-foreground"
          >
            <History className="h-3.5 w-3.5" />
            <span>Kartu Stok</span>
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onEditMinStock?.(item)}
            className="min-h-[40px] gap-1.5 text-xs text-muted-foreground hover:text-foreground"
          >
            <Pencil className="h-3.5 w-3.5" />
            <span>Edit Min</span>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function LocationQty({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <span className="block text-[10px] text-muted-foreground">{label}</span>
      <div className="flex justify-center font-mono font-medium">
        {children}
      </div>
    </div>
  );
}
